'use client';

import classNames from 'classnames';
import React, { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Tag from '@/components/molecules/tag/tag';
import AcceptTripInvitationModal from '@/components/organisms/accept-trip-invitation-modal/accept-trip-invitation-modal';
import SelectedPassengers from '@/components/organisms/selected-passengers/selected-passengers';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import styles from './profile-trip-invitation.module.scss';

export interface ProfileTripInvitationProps {
  /** The class name to apply */
  className?: string;
  /** Whether to hide the accept invitation action */
  hideAction?: boolean;
  /** Whether the card is fetching */
  isFetching?: boolean;
  /** Indicates whether the component is in a loading state */
  isLoading?: boolean;
  /** The trip that the user is invited to join */
  trip?: Content.Components['ProfileTripInvitationModuleDto']['trip'];
}

const ProfileTripInvitation = ({
  className,
  hideAction = false,
  isFetching = false,
  isLoading = false,
  trip,
}: ProfileTripInvitationProps) => {
  const { t } = useI18n();
  const { bindRef, isHovered } = useIsHovered<HTMLAnchorElement>();
  const [showAcceptInvitationModal, setShowAcceptInvitationModal] =
    useState(false);

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, className, styles.isLoading)}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.imageWrapper}>
            <SkeletonLoader height="100%" width="100%" />

            <SkeletonLoader
              className={classNames(styles.imageTag, styles.loader)}
              height="36px"
              width="100px"
            />
          </div>

          <div className={styles.content}>
            <div className={styles.topSection}>
              <div className={styles.header}>
                <SkeletonLoader
                  className={classNames(
                    styles.invitationHeadline,
                    styles.loader,
                  )}
                  height="34px"
                  width="70%"
                />
              </div>

              <SkeletonLoader
                className={classNames(styles.cardTag, styles.loader)}
                height="36px"
                width="100px"
              />
            </div>

            <div className={styles.middleSection}>
              <div className={styles.item}>
                <SkeletonLoader
                  className={styles.loader}
                  height="22px"
                  width="50%"
                />
              </div>

              <div className={styles.item}>
                <SkeletonLoader
                  className={styles.loader}
                  height="22px"
                  width="60%"
                />
              </div>
            </div>

            <div className={styles.bottomSection}>
              <SkeletonLoader
                className={classNames(styles.pax, styles.loader)}
                height="32px"
                width="20%"
              />

              <div className={styles.invitationDescription}>
                <SkeletonLoader
                  className={styles.loader}
                  height="22px"
                  width="60%"
                />
              </div>

              <SkeletonLoader
                borderRadius="999px"
                className={styles.buttonLoader}
                height="48px"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.component, className)}>
      {trip && (
        <React.Fragment>
          <Link
            ref={bindRef}
            className={styles.contentWrapper}
            onClick={
              !hideAction ? () => setShowAcceptInvitationModal(true) : undefined
            }
          >
            <div className={styles.imageWrapper}>
              {trip.image && (
                <Image
                  alt={trip.image.altText}
                  src={trip.image.url}
                  srcSet={{
                    tablet: {
                      width: 310,
                    },
                  }}
                  unoptimized
                  width={700}
                />
              )}

              {trip.invitationTagText && (
                <Tag
                  className={styles.imageTag}
                  size="large"
                  text={trip.invitationTagText}
                  variant="quaternary"
                />
              )}

              <div className={styles.overlay}>
                {trip.invitationHeadline && (
                  <Typography
                    className={styles.invitationHeadlineOverlay}
                    tagStyle="headingMedium"
                  >
                    {trip.invitationHeadline}
                  </Typography>
                )}

                <div className={styles.overlayContent}>
                  <div className={styles.overlayDate}>
                    <Icon name="CheckIn" size={16} />

                    <Typography tagStyle="bodyMediumBold">
                      {trip.outboundDepartureDate}
                    </Typography>
                  </div>

                  <div className={styles.divider} />

                  <div className={styles.overlayDate}>
                    <Icon name="CheckOut" size={16} />

                    <Typography tagStyle="bodyMediumBold">
                      {trip.homeboundDepartureDate}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.content}>
              {(trip.invitationHeadline || trip.invitationTagText) && (
                <div className={styles.topSection}>
                  {trip.invitationHeadline && (
                    <div className={styles.header}>
                      {trip.invitationHeadline && (
                        <Typography
                          className={styles.invitationHeadline}
                          tagStyle="headingMedium"
                        >
                          {trip.invitationHeadline}
                        </Typography>
                      )}
                    </div>
                  )}

                  {trip.invitationTagText && (
                    <Tag
                      className={styles.cardTag}
                      size="small"
                      text={trip.invitationTagText}
                      variant="quaternary"
                    />
                  )}
                </div>
              )}

              {(trip.durationText || trip.invitationDescription) && (
                <div className={styles.middleSection}>
                  {trip.durationText && (
                    <div className={styles.item}>
                      <Icon name="Calendar" size={20} />

                      <Typography
                        className={styles.durationText}
                        tagStyle="bodyMedium"
                      >
                        {trip.durationText}
                      </Typography>
                    </div>
                  )}

                  {trip.invitationDescription && (
                    <div className={styles.item}>
                      <Typography className={styles.text} tagStyle="bodySmall">
                        {trip.invitationDescription}
                      </Typography>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.bottomSection}>
                {trip.passengers && (
                  <div className={styles.pax}>
                    <Typography
                      className={styles.traveler}
                      tagStyle="bodySmall"
                    >
                      {`${t('items.profile.profilePage.coTravelers.traveler')}:`}
                    </Typography>

                    {isFetching ? (
                      <SkeletonLoader
                        borderRadius="999px"
                        className={styles.loader}
                        height="32px"
                        width="96px"
                      />
                    ) : (
                      <SelectedPassengers
                        limit={4}
                        passengers={trip.passengers}
                        size="large"
                      />
                    )}
                  </div>
                )}

                {trip.invitationDescription && (
                  <div className={styles.invitationDescription}>
                    <Typography className={styles.text} tagStyle="bodySmall">
                      {trip.invitationDescription}
                    </Typography>
                  </div>
                )}

                {!hideAction && (
                  <Button
                    className={styles.button}
                    hovered={isHovered}
                    renderAs="div"
                    text={t(
                      'items.profile.profilePage.coTravelers.acceptInvitation',
                    )}
                    variant="primary"
                  />
                )}
              </div>
            </div>
          </Link>

          <AcceptTripInvitationModal
            description={trip.overlayDescription}
            encryptedBookingHash={trip.encryptedBookingHash}
            headline={trip.overlayHeadline}
            isOpen={showAcceptInvitationModal}
            onClose={() => setShowAcceptInvitationModal(false)}
            ownerId={trip.ownerId}
            passengers={trip.passengers}
            preHeadline={trip.overlayPreHeadline}
            tooltipText={trip.overlayTooltipText}
          />
        </React.Fragment>
      )}
    </div>
  );
};

ProfileTripInvitation.displayName = 'ProfileTripInvitation';

export default ProfileTripInvitation;
